const componentKey = componentId => String(componentId)

const substanceId = substanceOrContainer => {
    const substance = substanceOrContainer && substanceOrContainer.substance
        ? substanceOrContainer.substance
        : substanceOrContainer

    return substance && substance.id
}

const sameSubstance = (left, right) => String(substanceId(left)) === String(substanceId(right))

const questionStateProduct = position => ({
    ...(position.component || {}),
    substanceListQuestionStates: Array.isArray(position.substanceListQuestionStates)
        ? position.substanceListQuestionStates
        : [],
})

const safetyInstructions = position => {
    const instructions = position.safetyInstructions || {}
    const safeUseDocument = instructions.safeUseDocument || {}
    const disassemblingDocument = instructions.disassemblingDocument || {}

    return {
        isSafeUseApplicable: typeof instructions.isSafeUseApplicable === 'boolean'
            ? instructions.isSafeUseApplicable
            : null,
        safeUseDocumentId: safeUseDocument.id || null,
        safeUseDescription: safeUseDocument.description || '',
        disassemblingDocumentId: disassemblingDocument.id || null,
        disassemblingDescription: disassemblingDocument.description || '',
    }
}

const createComplexComplianceCompositionStore = () => {
    let snapshots = {}
    let drafts = {}

    const requireSnapshot = componentId => {
        const snapshot = snapshots[componentKey(componentId)]
        if (!snapshot) {
            throw new Error(`Complex compliance composition is missing component ${componentId}`)
        }
        return snapshot
    }

    const resolveSnapshot = (componentId, draft) => {
        if (draft === true) {
            const editorDraft = drafts[componentKey(componentId)]
            if (!editorDraft) {
                throw new Error(`Complex compliance editor is not open for component ${componentId}`)
            }
            return editorDraft
        }
        return requireSnapshot(componentId)
    }

    const replace = positions => {
        const nextSnapshots = {}
        const normalized = (positions || []).map(position => {
            const key = componentKey(position.component && position.component.id)
            let snapshot = nextSnapshots[key]

            if (!snapshot) {
                const product = questionStateProduct(position)
                snapshot = {
                    product,
                    included: Array.isArray(position.substances) ? position.substances : [],
                    excluded: Array.isArray(position.excludedSubstances)
                        ? position.excludedSubstances
                        : [],
                    safetyInstructions: safetyInstructions(position),
                }
                nextSnapshots[key] = snapshot
            }

            return {
                ...position,
                component: snapshot.product,
                substanceListQuestionStates: snapshot.product.substanceListQuestionStates,
                substances: snapshot.included,
                excludedSubstances: snapshot.excluded,
                safetyInstructions: snapshot.safetyInstructions,
            }
        })

        snapshots = nextSnapshots
        drafts = {}
        return normalized
    }

    const getEditorData = componentId => {
        const snapshot = requireSnapshot(componentId)
        const draft = {
            product: snapshot.product,
            included: snapshot.included.map(container => ({
                ...container,
                properties: container.properties && { ...container.properties },
            })),
            excluded: snapshot.excluded.map(container => ({ ...container })),
        }
        drafts[componentKey(componentId)] = draft
        return {
            product: draft.product,
            included: draft.included,
            excluded: draft.excluded,
        }
    }

    const includeSubstance = (componentId, substance, lists = [], draft = false) => {
        const snapshot = resolveSnapshot(componentId, draft)
        const excludedIndex = snapshot.excluded.findIndex(item => sameSubstance(item, substance))
        if (excludedIndex !== -1) {
            snapshot.excluded.splice(excludedIndex, 1)
        }
        if (!snapshot.included.some(item => sameSubstance(item, substance))) {
            snapshot.included.push({
                status: 'included',
                substance,
                details: {},
                properties: {},
                lists,
            })
        }
    }

    const excludeSubstance = (componentId, substance, draft = false) => {
        const snapshot = resolveSnapshot(componentId, draft)
        const includedIndex = snapshot.included.findIndex(item => sameSubstance(item, substance))
        if (includedIndex !== -1) {
            snapshot.included.splice(includedIndex, 1)
        }
        if (!snapshot.excluded.some(item => sameSubstance(item, substance))) {
            snapshot.excluded.push({ status: 'excluded', substance })
        }
    }

    const makeSubstanceUndefined = (componentId, substance, draft = false) => {
        const snapshot = resolveSnapshot(componentId, draft)
        const includedIndex = snapshot.included.findIndex(item => sameSubstance(item, substance))
        if (includedIndex !== -1) {
            snapshot.included.splice(includedIndex, 1)
        }
        const excludedIndex = snapshot.excluded.findIndex(item => sameSubstance(item, substance))
        if (excludedIndex !== -1) {
            snapshot.excluded.splice(excludedIndex, 1)
        }
    }

    const updateQuestionState = (componentId, listId, state) => {
        const states = requireSnapshot(componentId).product.substanceListQuestionStates
        const current = states.find(item => String(item.list) === String(listId))
        if (current) {
            current.state = state
        } else {
            states.push({ list: listId, state })
        }
    }

    const updateSubstanceProperties = (componentId, substance, properties) => {
        const container = requireSnapshot(componentId).included
            .find(item => sameSubstance(item, substance))
        if (container) {
            container.properties = properties
        }
    }

    const replaceIdentifierGroup = (componentId, key, values = []) => {
        const product = requireSnapshot(componentId).product
        const identifiers = Array.isArray(product.identifiers) ? product.identifiers : []
        const retained = identifiers.filter(identifier => identifier.key !== key)
        identifiers.splice(0, identifiers.length, ...retained, ...values)
        if (!Array.isArray(product.identifiers)) {
            product.identifiers = identifiers
        }
    }

    const getSafetyInstructions = componentId => requireSnapshot(componentId).safetyInstructions

    const updateSafetyInstructions = (componentId, changes) => {
        Object.assign(requireSnapshot(componentId).safetyInstructions, changes)
    }

    const savePayload = (componentId, draft = false) => {
        const snapshot = resolveSnapshot(componentId, draft)
        return {
            included: snapshot.included.map(container => ({
                id: substanceId(container),
                properties: container.properties,
            })),
            excluded: snapshot.excluded.map(substanceId),
        }
    }

    return Object.freeze({
        replace,
        getEditorData,
        includeSubstance,
        excludeSubstance,
        makeSubstanceUndefined,
        updateQuestionState,
        updateSubstanceProperties,
        replaceIdentifierGroup,
        getSafetyInstructions,
        updateSafetyInstructions,
        savePayload,
    })
}

export { createComplexComplianceCompositionStore }
export default createComplexComplianceCompositionStore
